import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/supabase';
import { ProspectTitle, PlaceOfWork, COMPANY_LICENSES } from '../../types';
import { ChevronDownIcon, XMarkIcon, ArrowLeftIcon, UserPlusIcon } from '@heroicons/react/24/outline';

const ProspectForm: React.FC = () => {
  const navigate = useNavigate();
  const { loanOfficer, isVA } = useAuth();
  const navigation = getAdminNavigation(isVA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    preferred_first_name: '',
    title: 'Sr Mortgage Broker' as ProspectTitle,
    phone: '',
    email: '',
    nmls: '',
    expected_month: '',
    expected_start_date: '',
    state_licenses: [] as string[],
    place_of_work: '' as PlaceOfWork | '',
    notes: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const [licenseDropdownOpen, setLicenseDropdownOpen] = useState(false);
  const licenseDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (licenseDropdownRef.current && !licenseDropdownRef.current.contains(event.target as Node)) {
        setLicenseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLicenseToggle = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      state_licenses: prev.state_licenses.includes(state)
        ? prev.state_licenses.filter((s) => s !== state)
        : [...prev.state_licenses, state],
    }));
  };

  const handleRemoveLicense = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      state_licenses: prev.state_licenses.filter((s) => s !== state),
    }));
  };

  const validateForm = () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First and last name are required');
      return false;
    }
    if (!formData.phone.trim() && !formData.email.trim()) {
      setError('Provide at least one contact method');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;
    if (!loanOfficer?.id) {
      setError('User not authenticated');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createProspect({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        preferred_first_name: formData.preferred_first_name.trim() || undefined,
        title: formData.title,
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        nmls: formData.nmls.trim() || undefined,
        expected_month: formData.expected_month || undefined,
        expected_start_date: formData.expected_start_date || undefined,
        state_licenses: formData.state_licenses.length > 0 ? formData.state_licenses : undefined,
        place_of_work: formData.place_of_work || undefined,
        notes: formData.notes.trim() || undefined,
        created_by: loanOfficer.id,
      });
      navigate('/admin/prospects');
    } catch (err: any) {
      // Error handled
      setError(err.message || 'Failed to create prospect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminShell
      title="Add Prospect"
      description="Create a new recruiting record"
      nav={navigation}
      actions={null}
    >
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/admin/prospects')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-lumin-primary hover:text-[#014f70]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Pipeline
        </button>

        <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-lumin-primary/10 flex items-center justify-center">
              <UserPlusIcon className="h-6 w-6 text-lumin-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Prospect Information</h2>
              <p className="text-sm text-slate-500">Enter the prospect's basic details</p>
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-6">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                placeholder="John"
                required
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                placeholder="Doe"
                required
              />
            </div>

            {/* Preferred First Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Preferred First Name
              </label>
              <input
                type="text"
                name="preferred_first_name"
                value={formData.preferred_first_name}
                onChange={handleInputChange}
                placeholder="Nickname"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
              />
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
              <select
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary bg-white"
              >
                <option value="Sr Mortgage Broker">Sr Mortgage Broker</option>
                <option value="Executive Branch Manager">Executive Branch Manager</option>
              </select>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                placeholder="john@personal.com"
              />
            </div>

            {/* NMLS */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">NMLS</label>
              <input
                type="text"
                name="nmls"
                value={formData.nmls}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                placeholder="123456"
              />
            </div>

            {/* Work Location */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Work Location</label>
              <div className="flex gap-6 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="place_of_work"
                    value="In Office"
                    checked={formData.place_of_work === 'In Office'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-lumin-primary border-slate-300 focus:ring-lumin-primary"
                  />
                  <span className="text-sm text-slate-700">In Office</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="place_of_work"
                    value="Remote"
                    checked={formData.place_of_work === 'Remote'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-lumin-primary border-slate-300 focus:ring-lumin-primary"
                  />
                  <span className="text-sm text-slate-700">Remote</span>
                </label>
              </div>
            </div>

            {/* Expected Sponsor Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Expected Sponsor Date</label>
              <input
                type="date"
                name="expected_month"
                value={formData.expected_month}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
              />
            </div>

            {/* Expected Start Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Expected Start Date</label>
              <input
                type="date"
                name="expected_start_date"
                value={formData.expected_start_date}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
              />
              <p className="text-xs text-slate-500 mt-1">When they begin onboarding</p>
            </div>

            {/* State Licenses */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">State Licenses</label>
              <div className="relative" ref={licenseDropdownRef}>
                <div
                  onClick={() => setLicenseDropdownOpen(!licenseDropdownOpen)}
                  className="min-h-[50px] w-full rounded-xl border border-slate-200 px-4 py-3 cursor-pointer flex items-center justify-between gap-2 bg-white"
                >
                  <div className="flex flex-wrap gap-1 flex-1">
                    {formData.state_licenses.length === 0 ? (
                      <span className="text-slate-400">Select states...</span>
                    ) : (
                      formData.state_licenses.map((state) => (
                        <span
                          key={state}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lumin-primary/10 text-lumin-primary text-xs font-medium"
                        >
                          {state}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLicense(state);
                            }}
                            className="hover:bg-lumin-primary/20 rounded-full p-0.5"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${licenseDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
                {licenseDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {COMPANY_LICENSES.map((state) => (
                      <div
                        key={state}
                        onClick={() => handleLicenseToggle(state)}
                        className={`px-4 py-2 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                          formData.state_licenses.includes(state) ? 'bg-lumin-primary/5' : ''
                        }`}
                      >
                        <span className="text-sm text-slate-700">{state}</span>
                        {formData.state_licenses.includes(state) && (
                          <span className="text-lumin-primary text-sm">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {formData.state_licenses.length} state{formData.state_licenses.length !== 1 ? 's' : ''} selected
              </p>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Notes (internal)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes for VAs and admins..."
                rows={3}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary resize-none"
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin/prospects')}
              className="px-6 py-3 rounded-2xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 rounded-2xl bg-lumin-primary text-white font-semibold hover:bg-[#014f70] disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlusIcon className="h-5 w-5" />
                  Create Prospect
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminShell>
  );
};

export default ProspectForm;
